import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Sleep timer, and "Still watching?" (roadmap Section 7 step 6; owner, 2026-10-12).
 *
 * The timer stops playback after a set time, set from the player menu; the time left is
 * kept (localStorage) so it survives an app restart. "Still watching?" is a Settings choice,
 * off by default: after 2 or 4 hours with no button pressed while something plays, the same
 * dialog asks, and stops after a minute with no answer, which frees the provider connection
 * a forgotten TV would hold all night.
 */

/** The lengths the timer is offered in, minutes. The longest is 4 hours (owner, 2026-10-14):
 *  an evening's viewing ends long before the box would, and 2 hours was the shortest a
 *  late-night viewer could set and still wake up to a dark screen. */
export const SLEEP_CHOICES = [15, 30, 60, 90, 120, 240] as const;
export const IDLE_CHOICES = [0, 2, 4] as const; // hours; 0 = off, the default
export type IdleHours = (typeof IDLE_CHOICES)[number];
/** The warning, before the stop. */
export const WARN_MS = 60_000;

/**
 * How one length reads where space is tight — the player's menu, the corner, the Help list and
 * the remote's settings row. Minutes up to an hour, hours after that: "240 min" is not how
 * anyone says four hours.
 */
export function sleepLengthShort(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

/** The same length spelled out, for Settings' dropdown ("1 hour 30 minutes"). */
export function sleepLengthLong(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const spelledOut = `${hours} ${hours === 1 ? "hour" : "hours"}`;
  return rest === 0 ? spelledOut : `${spelledOut} ${rest} minutes`;
}

interface SleepStore {
  /** When playback stops (ms since epoch), or null when the timer is off. */
  endsAt: number | null;
  /** "Still watching?" after this many hours with no button pressed; 0 = off. */
  idleHours: IdleHours;
  setSleep: (minutes: number | null) => void;
  extend: (minutes: number) => void;
  setIdleHours: (hours: IdleHours) => void;
}

export const useSleep = create<SleepStore>()(
  persist(
    (set) => ({
      endsAt: null,
      idleHours: 0,
      setSleep: (minutes) => set({ endsAt: minutes ? Date.now() + minutes * 60_000 : null }),
      extend: (minutes) => set((state) => ({ endsAt: (state.endsAt ?? Date.now()) + minutes * 60_000 })),
      setIdleHours: (idleHours) => set({ idleHours }),
    }),
    {
      name: "slique-sleep",
      version: 1,
      // A timer that ran out while the app was closed is not carried into a new evening, and
      // a stored value is only ever one of the offered ones.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (state.endsAt !== null && (typeof state.endsAt !== "number" || state.endsAt < Date.now())) state.endsAt = null;
        if (!IDLE_CHOICES.includes(state.idleHours)) state.idleHours = 0;
      },
    },
  ),
);

export type SleepDecision = "none" | "warnSleep" | "stopSleep" | "warnIdle";

/** Pure: what to do now. `playing` is true while something plays, live or on demand. */
export function sleepDecision(input: {
  now: number;
  endsAt: number | null;
  lastInputAt: number;
  idleHours: number;
  playing: boolean;
}): SleepDecision {
  if (!input.playing) return "none";
  if (input.endsAt !== null) {
    if (input.now >= input.endsAt) return "stopSleep";
    if (input.endsAt - input.now <= WARN_MS) return "warnSleep";
  }
  if (input.idleHours > 0 && input.now - input.lastInputAt >= input.idleHours * 3_600_000) return "warnIdle";
  return "none";
}

// --- Knowing the viewer is there ----------------------------------------------------------

let lastInputAt = Date.now();

/** Someone is here: a key, a touch, or a phone's button. */
export function noteInput(): void {
  lastInputAt = Date.now();
}

export function lastInput(): number {
  return lastInputAt;
}

/**
 * Any key or touch counts as "someone is here". Capture and passive: it never changes what
 * the press does, and never stops or consumes it.
 */
export function installInputWatch(): () => void {
  const on = () => noteInput();
  window.addEventListener("keydown", on, true);
  window.addEventListener("pointerdown", on, { capture: true, passive: true });
  return () => {
    window.removeEventListener("keydown", on, true);
    window.removeEventListener("pointerdown", on, true);
  };
}
